import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Package, FileText, Calculator, Shield, Loader2, Zap, Star, Check, Truck, Headphones, MapPin, IndianRupee, Lock, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CountryCombobox } from './ui/country-combobox';
import { zoneFieldFor } from '../lib/destinationZones';
import { FlagIcon } from './ui/flag-icon';
import { computeQuoteHighlights, carrierDisplayName } from '../lib/quoteBadges';
import { CarrierLogo } from './ui/carrier-logo';
import JourneyBar from './JourneyBar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api, { quotesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Hero = () => {
  const { isAuthenticated } = useAuth();
  const [userDiscount, setUserDiscount] = useState(null); // { percentage, note }
  const [shipmentType, setShipmentType] = useState('parcel');
  const [quoteData, setQuoteData] = useState({
    from: 'IN',
    to: '',
    // Only asked for when the destination is priced by sub-zone
    toPostcode: '',
    weight: '',
    value: '',
    length: '',
    width: '',
    height: ''
  });
  
  // New states for instant quotes
  const [priorityCountries, setPriorityCountries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  // Restoring results from the URL: show the focused view straight away
  // instead of flashing the homepage while rates are re-fetched.
  const [restoringResults, setRestoringResults] = useState(
    () => new URLSearchParams(window.location.search).get('results') === 'true'
  );
  const quoteFormRef = useRef(null);
  const { toast } = useToast();

  // Function to get carrier logo
  const getCarrierLogo = (carrierName) => {
    if (!carrierName) return null;
    
    const carrierKey = carrierName.toLowerCase().replace(/\s+/g, '');
    const logoMap = {
      'indiapost': '/assets/images/carriers/indiapost-v2.svg',
      'sendmyparcel': '/assets/images/carriers/sendmyparcel.png',
      'arame': '/assets/images/carriers/arame.png',
      'bluedart': '/assets/images/carriers/bluedart.png',
      'dhl': '/assets/images/carriers/dhl.png',
      'dtdc': '/assets/images/carriers/dtdc.png',
      'fedex': '/assets/images/carriers/fedex.png',
      'ups': '/assets/images/carriers/ups.png',
      'xfas': '/assets/images/carriers/sendmyparcel.png'
    };
    
    // Try exact match first
    if (logoMap[carrierKey]) {
      return logoMap[carrierKey];
    }
    
    // Try partial matches for common variations
    for (const [key, logo] of Object.entries(logoMap)) {
      if (carrierKey.includes(key) || key.includes(carrierKey)) {
        return logo;
      }
    }
    
    return null;
  };

  // Load Priority Connections countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await quotesAPI.getPriorityConnectionsCountries();
        setPriorityCountries(response.countries || []);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  // Fetch user discount if logged in
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchDiscount = async () => {
      try {
        const response = await api.get('/bookings/my-discount');
        setUserDiscount(response.data.discount || null);
      } catch {
        setUserDiscount(null);
      }
    };
    fetchDiscount();
  }, [isAuthenticated]);

  // Prefill from URL parameters; if results were open (page refresh or a
  // shared link with results=true), restore the focused results view too.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.size > 0) {
      const newQuoteData = { ...quoteData };

      if (urlParams.get('from')) newQuoteData.from = urlParams.get('from');
      if (urlParams.get('to')) newQuoteData.to = urlParams.get('to');
      // Sub-zone (US state / postcode): without it the zoned SendMyParcel
      // services drop out of the restored results.
      if (urlParams.get('postcode')) newQuoteData.toPostcode = urlParams.get('postcode');
      if (urlParams.get('weight')) newQuoteData.weight = urlParams.get('weight');
      if (urlParams.get('value')) newQuoteData.value = urlParams.get('value');
      if (urlParams.get('length')) newQuoteData.length = urlParams.get('length');
      if (urlParams.get('width')) newQuoteData.width = urlParams.get('width');
      if (urlParams.get('height')) newQuoteData.height = urlParams.get('height');

      const urlType = urlParams.get('type') || shipmentType;
      if (urlParams.get('type')) {
        setShipmentType(urlType);
      }

      setQuoteData(newQuoteData);

      if (urlParams.get('results') === 'true' && newQuoteData.to) {
        // Re-fetch live rates and reopen the results view; the URL is kept
        // by fetchQuotes so another refresh still works.
        fetchQuotes(newQuoteData, urlType);
      } else {
        // Clear URL parameters after loading
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  // Back/Forward must move between the form and the results rather than
  // leaving the site, so mirror the URL into the view state.
  useEffect(() => {
    const syncWithUrl = () => {
      const open = new URLSearchParams(window.location.search).get('results') === 'true';
      setShowQuotes(open && quotes.length > 0);
      if (!open) setRestoringResults(false);
    };
    window.addEventListener('popstate', syncWithUrl);
    return () => window.removeEventListener('popstate', syncWithUrl);
  }, [quotes.length]);

  const handleStartShipping = () => {
    // Below lg the layout stacks and the quote card sits off-screen under the
    // hero copy — scroll to it instead of bouncing the user to login/register.
    if (window.innerWidth < 1024 && quoteFormRef.current) {
      quoteFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (isAuthenticated) {
      // If already logged in, go to dashboard to create shipment
      window.location.href = '/dashboard';
    } else {
      // If not logged in, trigger registration modal
      window.location.href = '/?register=true';
    }
  };

  // Query string that reproduces the current quote (refresh survival)
  const buildResultsParams = (qd, type) => {
    const p = new URLSearchParams();
    if (qd.from) p.set('from', qd.from);
    if (qd.to) p.set('to', qd.to);
    if (qd.toPostcode) p.set('postcode', qd.toPostcode);
    if (qd.weight) p.set('weight', qd.weight);
    if (qd.value) p.set('value', qd.value);
    if (qd.length) p.set('length', qd.length);
    if (qd.width) p.set('width', qd.width);
    if (qd.height) p.set('height', qd.height);
    p.set('type', type);
    p.set('results', 'true');
    return p;
  };

  const fetchQuotes = async (qd, type, { pushHistory = false } = {}) => {
    // For document shipments, weight is optional (will use default)
    const finalWeight = type === 'document' ? 0.5 : (parseFloat(qd.weight) || 0.5);

    setLoading(true);
    setShowQuotes(false);

    try {
      const requestData = {
        from_country: qd.from,
        to_country: qd.to,
        // Ignored by the backend unless the destination is postcode-zoned
        to_postal_code: qd.toPostcode || null,
        weight: finalWeight,
        shipment_type: type,
        declared_value: parseFloat(qd.value) || 0,
        // Send dimensions only if they are provided (not empty or zero)
        length: qd.length && parseFloat(qd.length) > 0 ? parseFloat(qd.length) : null,
        width: qd.width && parseFloat(qd.width) > 0 ? parseFloat(qd.width) : null,
        height: qd.height && parseFloat(qd.height) > 0 ? parseFloat(qd.height) : null,
        packages: [{
          id: 1,
          weight: finalWeight,
          // Use provided dimensions or defaults based on shipment type
          length: (qd.length && parseFloat(qd.length) > 0) ? parseFloat(qd.length) :
                  (type === 'document' ? 21 : 30),
          width: (qd.width && parseFloat(qd.width) > 0) ? parseFloat(qd.width) :
                 (type === 'document' ? 29.7 : 20),
          height: (qd.height && parseFloat(qd.height) > 0) ? parseFloat(qd.height) :
                  (type === 'document' ? 1 : 10),
          quantity: 1
        }],
        content_items: [{
          id: 1,
          description: type === 'document' ? 'Documents' : 'General Items',
          quantity: 1,
          value: parseFloat(qd.value) || (type === 'document' ? 500 : 100),
          hsn_code: ''
        }]
      };

      const response = await quotesAPI.createQuote(requestData);

      if (response.carrier_quotes && response.carrier_quotes.length > 0) {
        setQuotes(response.carrier_quotes);
        setShowQuotes(true);
        setRestoringResults(false);
        // Keep the inputs in the URL while results are open so a page refresh
        // restores this view. A fresh search PUSHES an entry, so browser Back
        // returns to the form instead of leaving the site; restoring from a URL
        // must not push, or Back would just re-open the same results.
        const resultsUrl = `${window.location.pathname}?${buildResultsParams(qd, type).toString()}`;
        if (pushHistory) {
          window.history.pushState({ smpResults: true }, document.title, resultsUrl);
        } else {
          window.history.replaceState({ smpResults: true }, document.title, resultsUrl);
        }
        toast({
          title: "Quotes Generated",
          description: `Found ${response.carrier_quotes.length} shipping options`
        });
      } else {
        toast({
          title: "No Quotes Available",
          description: "No carriers available for this route. Please try different options.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Quote error:', error);
      toast({
        title: "Quote Failed",
        description: error.response?.data?.detail || "Failed to get quotes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setRestoringResults(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();

    // Validation, in the order the form reads: destination first, then weight
    const focusField = (id) => {
      const el = document.getElementById(id);
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    if (!quoteData.to) {
      toast({
        title: "Destination required",
        description: "Please choose where your shipment is going.",
        variant: "destructive"
      });
      focusField(shipmentType === 'document' ? 'to-doc' : 'to');
      return;
    }

    // For parcel shipments, weight is required
    if (shipmentType === 'parcel' && !quoteData.weight) {
      toast({
        title: "Weight required",
        description: "Please enter the package weight to compare rates.",
        variant: "destructive"
      });
      focusField('weight');
      return;
    }

    await fetchQuotes(quoteData, shipmentType, { pushHistory: true });
  };

  // Lock page scroll behind the focused results overlay
  useEffect(() => {
    document.body.style.overflow = showQuotes || restoringResults ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showQuotes, restoringResults]);

  const destinationCountry = priorityCountries.find(
    (c) => (c.code || c.countryCode) === quoteData.to
  );
  const destinationName =
    destinationCountry?.name || destinationCountry?.countryName || 'Destination';

  const displayWeight =
    shipmentType === 'document' ? quoteData.weight || '0.5' : quoteData.weight || '0';

  const sortedQuotes = [...quotes].sort((a, b) => a.total_cost - b.total_cost);

  // Badge rules live in lib/quoteBadges so this widget, the quote page and the
  // booking flow label the same options the same way - including ranking on
  // the platform's TAT when it supplies one.
  const { fastestIdx, recommendedIdx } = computeQuoteHighlights(sortedQuotes);


  // Short descriptor + the one included-feature line shown on each card
  const carrierMeta = (name) => {
    const c = (name || '').toLowerCase();

    // Our own service comes in DDP and non-DDP flavours: only the DDP one has
    // destination duties & taxes in the quoted price.
    if (c.includes('sendmyparcel') || c.includes('xfas')) {
      const ddp = c.includes('ddp');
      return ddp
        ? { tagline: 'Delivered Duty Paid', feature: 'Duties & taxes included', dutyIncluded: true }
        : { tagline: 'Our own network', feature: 'Duties & taxes extra', dutyIncluded: false };
    }

    // India Post is a postal service, not a courier: the recipient pays duty on
    // arrival, and ordinary Air Parcel is not tracked end to end.
    if (c.includes('india post')) {
      const air = /air\s*parcel/.test(c);
      return {
        tagline: c.includes('ems') ? 'Fastest postal service' : (air ? 'Economy air' : 'Economy tracked'),
        feature: 'Duties & taxes extra',
        dutyIncluded: false,
      };
    }

    if (c.includes('ups')) return { tagline: 'Reliable & Global', feature: 'Tracking included', dutyIncluded: true };
    if (c.includes('dhl')) return { tagline: 'Fast & Express Delivery', feature: 'Priority handling', dutyIncluded: true };
    if (c.includes('fedex')) return { tagline: 'Dependable Worldwide', feature: 'Real-time tracking', dutyIncluded: true };
    if (c.includes('aramex')) return { tagline: 'Trusted global network', feature: 'Tracking included', dutyIncluded: true };
    return { tagline: 'International courier', feature: 'Tracking included', dutyIncluded: true };
  };

  const cardBadges = (index) => {
    const badges = [];
    if (index === 0) badges.push({ label: 'Best price', cls: 'bg-[#F17221]' });
    if (index === fastestIdx) badges.push({ label: 'Fastest delivery', cls: 'bg-green-600' });
    if (index === recommendedIdx) badges.push({ label: 'Recommended', cls: 'bg-[#0A1128]' });
    return badges;
  };

  const discountedPrice = (cost) =>
    Math.round(cost * (1 - (userDiscount?.percentage || 0) / 100));

  const fmtINR = (n) => Math.round(n).toLocaleString('en-IN');

  // Continue the journey on the /quote page with the current details prefilled
  const goToQuotePage = (carrierName) => {
    const searchParams = new URLSearchParams();
    if (carrierName) searchParams.set('carrier', carrierName);
    if (quoteData.from) searchParams.set('from', quoteData.from);
    if (quoteData.to) searchParams.set('to', quoteData.to);
    // The sub-zone prices the zoned SendMyParcel services; without it the
    // carrier picked here is missing from the /quote page's results.
    if (quoteData.toPostcode) searchParams.set('postcode', quoteData.toPostcode);
    const finalWeight =
      shipmentType === 'document' ? quoteData.weight || '0.5' : quoteData.weight;
    if (finalWeight) searchParams.set('weight', finalWeight);
    if (quoteData.value) searchParams.set('value', quoteData.value);
    searchParams.set('type', shipmentType);
    if (quoteData.length && parseFloat(quoteData.length) > 0) {
      searchParams.set('length', quoteData.length);
    }
    if (quoteData.width && parseFloat(quoteData.width) > 0) {
      searchParams.set('width', quoteData.width);
    }
    if (quoteData.height && parseFloat(quoteData.height) > 0) {
      searchParams.set('height', quoteData.height);
    }
    if (shipmentType === 'document') {
      if (!quoteData.length) searchParams.set('length', '21');
      if (!quoteData.width) searchParams.set('width', '29.7');
      if (!quoteData.height) searchParams.set('height', '1');
    }
    searchParams.set('autoSubmit', 'true');
    window.location.href = `/quote?${searchParams.toString()}`;
  };

  return (
    <section id="get-quote" className="relative bg-gradient-to-br from-slate-50 to-blue-50 py-16 lg:py-24">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23F17221%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Hero Text */}
          <div className="text-left">
            <div className="inline-flex items-center space-x-2 bg-[#F17221]/10 text-[#F17221] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="h-4 w-4" />
              <span>Trusted by 10,000+ Businesses</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold text-[#0A1128] leading-[1.1] mb-6 tracking-tight">
              International Courier
              <span className="text-[#F17221] block mt-2">Services from India</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-lg font-medium">
              Send parcels and documents from India to 190+ countries. Compare DHL, FedEx, UPS, India Post and SendMyParcel rates, book with door pickup, and track every parcel to delivery.
            </p>

            {/* Key Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center space-x-3">
                <div className="bg-[#F17221]/10 p-2 rounded-lg">
                  <Calculator className="h-5 w-5 text-[#F17221]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A1128]">Save up to 25%</p>
                  <p className="text-sm text-slate-600">Compare & choose best rates</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="bg-[#0A1128]/10 p-2 rounded-lg">
                  <Package className="h-5 w-5 text-[#0A1128]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A1128]">190+ Countries</p>
                  <p className="text-sm text-slate-600">Worldwide delivery network</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <Button
                size="lg"
                className="bg-[#F17221] hover:brightness-110 text-white px-8 border border-[#F17221]"
                onClick={handleStartShipping}
              >
                Start Shipping
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Right Column - Quote Form */}
          <div ref={quoteFormRef} className="lg:justify-self-end w-full max-w-md scroll-mt-24">
            <Card className="bg-white/80 backdrop-blur-xl shadow-2xl shadow-[#0A1128]/15 border border-white/40 rounded-3xl">
              <CardHeader className="pb-5 border-b border-gray-100/50 bg-white/50 mb-4 px-6 pt-6">
                <CardTitle className="text-2xl font-extrabold text-[#0A1128]">Get Instant Quote</CardTitle>
                <p className="text-sm font-medium text-slate-500 mt-1">Compare rates instantly from premium carriers</p>
              </CardHeader>
              
              <CardContent>
                
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    {/* Shipment Type */}
                    <Tabs value={shipmentType} onValueChange={setShipmentType} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="parcel" className="flex items-center space-x-2">
                          <Package className="h-4 w-4" />
                          <span>Parcel</span>
                        </TabsTrigger>
                        <TabsTrigger value="document" className="flex items-center space-x-2">
                          <FileText className="h-4 w-4" />
                          <span>Document</span>
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="parcel" className="space-y-4 mt-4">
                        {/* From/To */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="from" className="text-sm font-medium">From</Label>
                            <Select value={quoteData.from} onValueChange={(value) => setQuoteData({...quoteData, from: value})}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="IN">🇮🇳 India</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="to" className="text-sm font-medium">To</Label>
                            <CountryCombobox
                              id="to"
                              countries={priorityCountries}
                              loading={loadingCountries}
                              value={quoteData.to}
                              onChange={(code) => setQuoteData({...quoteData, to: code})}
                              placeholder="Destination"
                            />
                          </div>
                          {zoneFieldFor(quoteData.to) && (
                            <div className="col-span-2 mt-3">
                              <Label htmlFor="to-postcode" className="text-sm font-medium">
                                {zoneFieldFor(quoteData.to).label}
                              </Label>
                              {zoneFieldFor(quoteData.to).options ? (
                                // Same type-ahead as the destination field: 56 states
                                // is a long scroll, and the customer knows the name.
                                <CountryCombobox
                                  id="to-postcode"
                                  countries={zoneFieldFor(quoteData.to).options.map(([code, name]) => ({ code, name }))}
                                  value={quoteData.toPostcode}
                                  onChange={(code) => setQuoteData({ ...quoteData, toPostcode: code })}
                                  placeholder="Search a state"
                                />
                              ) : (
                                <Input
                                  id="to-postcode"
                                  type="text"
                                  inputMode={zoneFieldFor(quoteData.to).mode}
                                  maxLength={zoneFieldFor(quoteData.to).maxLength}
                                  autoComplete="postal-code"
                                  placeholder={zoneFieldFor(quoteData.to).placeholder}
                                  value={quoteData.toPostcode}
                                  onChange={(e) => setQuoteData({ ...quoteData, toPostcode: e.target.value })}
                                />
                              )}
                              <p className="mt-1 text-[11px] text-slate-400">
                                {zoneFieldFor(quoteData.to).hint}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Weight + dimensions on one compact row */}
                        <div>
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <Label htmlFor="weight" className="text-xs font-medium">
                                Weight (kg) *
                              </Label>
                              <Input
                                id="weight"
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                min="0"
                                placeholder="1.0"
                                className="px-2 text-sm"
                                value={quoteData.weight}
                                onChange={(e) => setQuoteData({...quoteData, weight: e.target.value})}
                              />
                            </div>
                            <div>
                              <Label htmlFor="length" className="text-xs font-medium">Length</Label>
                              <Input
                                id="length"
                                type="number"
                                inputMode="numeric"
                                placeholder="cm"
                                className="px-2 text-sm"
                                value={quoteData.length}
                                onChange={(e) => setQuoteData({...quoteData, length: e.target.value})}
                                min="0"
                                step="1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="width" className="text-xs font-medium">Width</Label>
                              <Input
                                id="width"
                                type="number"
                                inputMode="numeric"
                                placeholder="cm"
                                className="px-2 text-sm"
                                value={quoteData.width}
                                onChange={(e) => setQuoteData({...quoteData, width: e.target.value})}
                                min="0"
                                step="1"
                              />
                            </div>
                            <div>
                              <Label htmlFor="height" className="text-xs font-medium">Height</Label>
                              <Input
                                id="height"
                                type="number"
                                inputMode="numeric"
                                placeholder="cm"
                                className="px-2 text-sm"
                                value={quoteData.height}
                                onChange={(e) => setQuoteData({...quoteData, height: e.target.value})}
                                min="0"
                                step="1"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Dimensions optional - volumetric weight is calculated when provided
                          </p>
                        </div>
                      </TabsContent>

                      <TabsContent value="document" className="space-y-4 mt-4">
                        {/* From/To for Documents */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="from-doc" className="text-sm font-medium">From</Label>
                            <Select value={quoteData.from} onValueChange={(value) => setQuoteData({...quoteData, from: value})}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="IN">🇮🇳 India</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="to-doc" className="text-sm font-medium">To</Label>
                            <CountryCombobox
                              id="to-doc"
                              countries={priorityCountries}
                              loading={loadingCountries}
                              value={quoteData.to}
                              onChange={(code) => setQuoteData({...quoteData, to: code})}
                              placeholder="Destination"
                            />
                          </div>
                          {zoneFieldFor(quoteData.to) && (
                            <div className="col-span-2 mt-3">
                              <Label htmlFor="to-postcode-doc" className="text-sm font-medium">
                                {zoneFieldFor(quoteData.to).label}
                              </Label>
                              {zoneFieldFor(quoteData.to).options ? (
                                // Same type-ahead as the destination field: 56 states
                                // is a long scroll, and the customer knows the name.
                                <CountryCombobox
                                  id="to-postcode-doc"
                                  countries={zoneFieldFor(quoteData.to).options.map(([code, name]) => ({ code, name }))}
                                  value={quoteData.toPostcode}
                                  onChange={(code) => setQuoteData({ ...quoteData, toPostcode: code })}
                                  placeholder="Search a state"
                                />
                              ) : (
                                <Input
                                  id="to-postcode-doc"
                                  type="text"
                                  inputMode={zoneFieldFor(quoteData.to).mode}
                                  maxLength={zoneFieldFor(quoteData.to).maxLength}
                                  autoComplete="postal-code"
                                  placeholder={zoneFieldFor(quoteData.to).placeholder}
                                  value={quoteData.toPostcode}
                                  onChange={(e) => setQuoteData({ ...quoteData, toPostcode: e.target.value })}
                                />
                              )}
                              <p className="mt-1 text-[11px] text-slate-400">
                                {zoneFieldFor(quoteData.to).hint}
                              </p>
                            </div>
                          )}
                        </div>

                        <p className="text-xs text-slate-500">
                          Documents are quoted at a standard 0.5 kg.
                        </p>
                      </TabsContent>
                    </Tabs>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#F17221] hover:bg-[#d9661d] text-white font-bold py-4 rounded-xl shadow-lg shadow-[#F17221]/30 transition-all duration-300 transform hover:-translate-y-1"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Getting Quotes...
                        </>
                      ) : (
                        <>
                          Get Instant Quote
                          <Calculator className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Focused results: full-screen journey view - logo + back only, no site nav */}
      {(showQuotes || restoringResults) && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-gradient-to-br from-slate-50 to-blue-50">
          <JourneyBar onBack={() => {
              // If we pushed a results entry, go back so history stays linear;
              // otherwise (deep link) just clean the URL in place.
              if (window.history.state && window.history.state.smpResults) {
                window.history.back();
              } else {
                setShowQuotes(false);
                window.history.replaceState({}, document.title, window.location.pathname);
              }
            }}
            backLabel="Edit details" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
            {/* Route header */}
            <div className="text-center pt-6 pb-5">
              <p className="flex items-center justify-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-[0.2em]">
                <Package className="h-4 w-4 text-slate-400" aria-hidden="true" />
                Sending {parseFloat(displayWeight || 0).toFixed(1)} kg &bull; {shipmentType}
              </p>
              <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-5">
                <span className="inline-flex items-center gap-3">
                  <FlagIcon country="IN" className="h-6 sm:h-7" />
                  <span className="text-xl sm:text-3xl font-extrabold tracking-tight text-[#0A1128]">India</span>
                </span>
                <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 rotate-90 text-slate-400 sm:rotate-0" aria-hidden="true" />
                <span className="inline-flex items-center gap-3">
                  <FlagIcon country={destinationName} className="h-6 sm:h-7" />
                  <span className="text-xl sm:text-3xl font-extrabold tracking-tight text-[#0A1128]">{destinationName}</span>
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                {sortedQuotes.length === 0
                  ? 'Fetching live rates from our carriers...'
                  : 'Compare trusted couriers and book your preferred option'}
              </p>
              {sortedQuotes.length > 0 &&
              sortedQuotes[0].volumetric_weight > 0 &&
              sortedQuotes[0].volumetric_weight > parseFloat(quoteData.weight || 0) ? (
                <p className="text-xs text-slate-500 mt-3">
                  Volumetric weight of {Math.ceil(sortedQuotes[0].volumetric_weight)} kg applies for pricing
                </p>
              ) : null}
            </div>

            {/* Reassurance strip */}
            <div className="rounded-2xl border border-slate-200 bg-white/70 px-5 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { Icon: Shield, tint: 'bg-blue-50 text-blue-600', title: 'Trusted & Secure', sub: '100% safe delivery' },
                  { Icon: Headphones, tint: 'bg-emerald-50 text-emerald-600', title: '24/7 Support', sub: 'We are here to help' },
                  { Icon: MapPin, tint: 'bg-violet-50 text-violet-600', title: 'Live Tracking', sub: 'Track your parcel anytime' },
                  { Icon: IndianRupee, tint: 'bg-orange-50 text-[#F17221]', title: 'Best Price Guarantee', sub: 'Get the best rates always' },
                ].map(({ Icon, tint, title, sub }) => (
                  <div key={title} className="flex items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tint}`}>
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-bold text-[#0A1128]">{title}</span>
                      <span className="block text-[12px] text-slate-500">{sub}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Carrier cards: 4 across on desktop, 1 per row on mobile */}
            <div className="mt-6 grid grid-cols-1 gap-4 pt-3 sm:grid-cols-2 lg:grid-cols-4">
              {sortedQuotes.length === 0 &&
                [1, 2, 3, 4].map((i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="animate-pulse rounded-2xl border border-slate-200 bg-white p-5"
                  >
                    <div className="h-10 w-[120px] rounded bg-slate-100" />
                    <div className="mt-4 h-4 w-2/3 rounded bg-slate-100" />
                    <div className="mt-2 h-3 w-1/2 rounded bg-slate-100" />
                    <div className="mt-5 h-3 w-1/3 rounded bg-slate-100" />
                    <div className="mt-3 h-7 w-1/2 rounded bg-slate-100" />
                    <div className="mt-5 h-10 w-full rounded-lg bg-slate-100" />
                  </div>
                ))}
              {sortedQuotes.map((quote, index) => {
                const meta = carrierMeta(quote.carrier_name);
                const isRec = index === recommendedIdx;
                const isFastest = index === fastestIdx && !isRec;
                const isCheapest = index === 0;
                return (
                  <div
                    key={`${quote.carrier_name}-${index}`}
                    className={`relative flex h-full flex-col rounded-2xl bg-white p-5 transition-shadow ${
                      isRec
                        ? 'border-2 border-[#F17221] shadow-[0_8px_24px_-16px_rgba(241,114,33,0.8)]'
                        : 'border border-slate-200 hover:shadow-md'
                    }`}
                  >
                    {(isRec || isFastest) && (
                      <span
                        className={`absolute -top-3 left-4 rounded-md px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm ${
                          isRec ? 'bg-[#F17221]' : 'bg-[#0A1128]'
                        }`}
                      >
                        {isRec ? 'Recommended' : 'Fastest'}
                      </span>
                    )}
                    {isCheapest && (
                      <span className="absolute right-4 top-4 rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">
                        Best price
                      </span>
                    )}

                    <CarrierLogo
                      src={getCarrierLogo(quote.carrier_name)}
                      name={quote.carrier_name}
                      className="mt-2 h-10 w-[120px]"
                    />

                    <p className="mt-4 truncate text-[16px] font-extrabold leading-tight text-[#0A1128]">
                      {carrierDisplayName(quote.carrier_name)}
                    </p>
                    <p className="mt-1 text-[13px] text-slate-500">{meta.tagline}</p>

                    <div className="my-4 border-t border-dashed border-slate-200" />

                    <p className="text-[12px] text-slate-400">From</p>
                    {userDiscount && userDiscount.percentage > 0 ? (
                      <p className="flex items-baseline gap-2">
                        <span className="text-[13px] tabular-nums text-slate-400 line-through">
                          &#8377;{fmtINR(quote.total_cost)}
                        </span>
                        <span className="text-[26px] font-extrabold leading-none tabular-nums tracking-tight text-emerald-600">
                          &#8377;{fmtINR(discountedPrice(quote.total_cost))}
                        </span>
                      </p>
                    ) : (
                      <p
                        className={`text-[26px] font-extrabold leading-none tabular-nums tracking-tight ${
                          isRec ? 'text-[#F17221]' : 'text-[#0A1128]'
                        }`}
                      >
                        &#8377;{fmtINR(quote.total_cost)}
                      </p>
                    )}
                    <p className="mb-4 mt-1 text-[11px] text-slate-400">Shipping charge</p>

                    <button
                      type="button"
                      onClick={() => goToQuotePage(quote.carrier_name)}
                      aria-label={`Book now with ${carrierDisplayName(quote.carrier_name)}`}
                      className={`mt-auto inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg text-[13px] font-extrabold transition-colors ${
                        isRec
                          ? 'bg-[#F17221] text-white hover:bg-[#d9661d]'
                          : 'border border-blue-200 bg-white text-blue-700 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      Book Now
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>

                    <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[12px] text-slate-500">
                      {meta.dutyIncluded ? (
                        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                      ) : (
                        <Info className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
                      )}
                      {meta.feature}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Footer: pricing caveat + payment reassurance */}
            <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-blue-50/40 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <Shield className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                <span>
                  <span className="block text-[13px] font-bold text-[#0A1128]">
                    Prices cover international shipping. Pickup charges, if any, are extra.
                  </span>
                  <span className="block text-[12px] text-slate-500">
                    Final charges confirmed at booking.
                  </span>
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-3 border-slate-200 lg:border-l lg:pl-4">
                  <Lock className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                  <span>
                    <span className="block text-[13px] font-bold text-[#0A1128]">Secure Payments</span>
                    <span className="block text-[12px] text-slate-500">100% secure checkout</span>
                  </span>
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {['VISA', 'mastercard', 'AMEX', 'RuPay', 'UPI'].map((m) => (
                    <span
                      key={m}
                      className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-extrabold tracking-tight text-slate-600"
                    >
                      {m}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;